<i18n src="./translations"></i18n>

<template>
  <div
    class="imageSlider"
    tabindex="0"
    @keydown.left.prevent="prev"
    @keydown.right.prevent="next"
  >
    <slot />

    <div class="imagesWrapper">
      <div v-if="items && items.length && items.length > 1" class="controls">
        <button
          type="button"
          class="control prev"
          :title="$t('image-slider.prev')"
          :aria-label="$t('image-slider.prev')"
          @click="prev"
        >
          <ChevronLeftSvg class="controlIcon" width="45" height="45" />
        </button>
        <button
          type="button"
          class="control next"
          :title="$t('image-slider.next')"
          :aria-label="$t('image-slider.next')"
          @click="next"
        >
          <ChevronRightSvg class="controlIcon" width="45" height="45" />
        </button>
      </div>

      <div
        v-if="extendedItems && extendedItems.length"
        :class="{ images: true, animate }"
        :style="{
          width: `calc(${extendedItems.length * 100}%)`,
          transform: `translate(-${(100 / extendedItems.length) * index}%, 0)`,
        }"
      >
        <button
          v-for="(item, itemIndex) in extendedItems"
          :key="
            item.full.product
              ? `full-${itemIndex}-${item.full.product.src}`
              : `full-${itemIndex}`
          "
          type="button"
          :class="{
            image: true,
            'image--active': index === itemIndex,
          }"
          :style="{ width: `${100 / extendedItems.length}%` }"
          @click="() => $emit('click-image')"
        >
          <component :is="item.full.component" v-bind="item.full" />
        </button>
      </div>
    </div>

    <button
      v-if="pagination && items && items.length > 1"
      type="button"
      class="paginationButton"
      :aria-label="$t('product-image-slider.pagination-label')"
      @click="() => $emit('click-image')"
    >
      <ul class="productImageSlider-pagination">
        <li
          v-for="(item, i) in items"
          :key="`pagination-dot-${i}-${
            item.thumbnail && item.thumbnail.image
              ? item.thumbnail.image.src
              : item.full && item.full.product
              ? item.full.product.src
              : i
          }`"
          :data-index="i"
          :class="{
            'productImageSlider-pagination-dot': true,
            'productImageSlider-pagination-dot--highlighted':
              currentItemIndex === i + 1,
          }"
        />
      </ul>
    </button>

    <div
      v-if="showThumbnails && items && items.length && items.length > 1"
      ref="thumbnails"
      class="thumbnails"
    >
      <button
        v-for="(item, itemIndex) in items"
        :key="`thumbnail-${
          item.thumbnail.image
            ? itemIndex + '-' + item.thumbnail.image.src
            : itemIndex
        }`"
        ref="thumbnailButtons"
        type="button"
        :class="{
          thumbnail: true,
          active: itemIndex + 1 === index,
        }"
        :aria-current="itemIndex + 1 === index ? 'true' : null"
        :title="`${$t('image-slider.element')} ${itemIndex + 1}`"
        @click="goto(itemIndex + 1)"
        @mouseover="goto(itemIndex + 1)"
      >
        <component :is="item.thumbnail.component" v-bind="item.thumbnail" />
      </button>
    </div>
  </div>
</template>

<script>
import ChevronLeftSvg from 'Components/00-generated/ChevronLeftSvg';
import ChevronRightSvg from 'Components/00-generated/ChevronRightSvg';
import ProductImageItem from 'Components/02-molecules/product-image-item/ProductImageItem';
import ProductImageThumbnail from 'Components/02-molecules/product-image-thumbnail/ProductImageThumbnail';
import NoImageSvg from 'Components/00-generated/NoImageSvg';

export default {
  name: 'Slider',
  components: {
    ChevronLeftSvg,
    ChevronRightSvg,
    ProductImageItem,
    ProductImageThumbnail,
    NoImageSvg,
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
    showThumbnails: {
      type: Boolean,
      default: true,
    },
    pagination: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      index: 1,
      nextIndex: null,
      animate: true,
    };
  },
  computed: {
    extendedItems() {
      if (!this.items || !this.items.length) {
        return [];
      }
      const first = this.items[0];
      const last = this.items[this.items.length - 1];
      return [last, ...this.items, first];
    },
    currentItemIndex() {
      if (!this.items || !this.items.length) return 1;
      if (this.index === 0) return this.items.length;
      if (this.index === this.extendedItems.length - 1) return 1;
      return this.index;
    },
  },
  watch: {
    nextIndex(newValue) {
      requestAnimationFrame(() => {
        this.animate = true;
        if (newValue !== null) {
          this.index = newValue;
          this.nextIndex = null;
        }
      });
    },
    index() {
      this.scrollActiveThumbnailIntoView();
    },
  },
  methods: {
    scrollActiveThumbnailIntoView() {
      this.$nextTick(() => {
        const buttons = this.$refs.thumbnailButtons || [];
        const active = buttons && buttons[this.currentItemIndex - 1];
        if (active && active.scrollIntoView) {
          active.scrollIntoView({
            behavior: 'smooth',
            inline: 'nearest',
            block: 'nearest',
          });
        }
      });
    },
    next() {
      requestAnimationFrame(() => {
        if (this.index === this.extendedItems.length - 2) {
          this.animate = false;
          this.index = 0;
        }
        this.nextIndex = this.index + 1;
        this.$emit('change-image', this.nextIndex);
      });
      this.scrollActiveThumbnailIntoView();
    },
    prev() {
      requestAnimationFrame(() => {
        if (this.index === 1) {
          this.animate = false;
          this.index = this.extendedItems.length - 1;
        }
        this.nextIndex = this.index - 1;
        this.$emit('change-image', this.nextIndex);
      });
      this.scrollActiveThumbnailIntoView();
    },
    goto(index) {
      this.index = index;
      this.$emit('change-image', this.index);
    },
  },
};
</script>

<style scoped lang="scss">
@import 'variables/breakpoints';
@import 'mixins';

.imageSlider {
  position: relative;
  max-width: 100vw;
}

.imagesWrapper {
  width: 100%;
  overflow: hidden;
  position: relative;
  border: none;
}

.images {
  display: flex;

  &.animate {
    transition: transform 0.3s ease-in-out;
  }
}

.image {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: var(--cursor, auto);
  height: var(--slider-image-height, auto);
}

.thumbnails {
  display: flex;
  position: relative;
  gap: var(--space-2--half);
  padding: var(--space-1--half) var(--space-2);
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x proximity;

  .thumbnail {
    padding: 0;
    width: 64px;
    height: 64px;
    margin: auto 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    outline: none;
    border-radius: var(--border-radius-default);
    flex: 0 0 auto;
    scroll-snap-align: start;

    &.active {
      border: 1px solid var(--color-secondary);
    }
  }
}

.controls {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.control {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 16px 0;
  cursor: pointer;
  outline: none;
  position: relative;
  z-index: 1;

  &.prev {
    margin-left: -1px;
  }

  &.next {
    margin-right: -2px;
  }
}

.controlIcon {
  color: #545456;
  border: none;
  background: var(--color-white);
}

@media (pointer: coarse) {
  .control {
    opacity: 1;
  }
}

/* Pagination inside Slider */
.paginationButton {
  padding: 0;
  cursor: pointer;
  outline: none;
  position: absolute;
  bottom: 68px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  background: transparent;
  border: none;
}

.productImageSlider-pagination {
  list-style-type: none;
  padding: 10px 0;

  .productImageSlider-pagination-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    margin: 0 3px;
    border-radius: 50%;
    background-color: var(--color-silver-lining);

    &--highlighted {
      background-color: var(--color-hippo-darker);
    }
  }
}
</style>
